import { useEffect, useRef, type RefObject } from 'react';
import { type ArticleCtaPosition } from './ctaPosition';
import { getArticleSlug } from './getArticleSlug';

export type CtaType = 'diagnosis' | 'monte_chat' | 'trial_class';

type Params = {
  ctaType?: CtaType;
  ctaPosition?: ArticleCtaPosition;
};

type UseCtaViewTrackingResult = RefObject<HTMLDivElement>;

// IAB の viewability 基準（面積の50%以上が1秒以上連続して表示）に合わせる。
// 中盤CTAは読者が高速スクロールで通過するだけのケースが多く、閾値なしで数えると
// 露出（分母）を過大計上して「露出者あたりクリック率」を低く見せてしまう。
const VISIBLE_RATIO_THRESHOLD = 0.5;
const MIN_VISIBLE_DURATION_MS = 1000;

export const useCtaViewTracking = ({ ctaType, ctaPosition }: Params): UseCtaViewTrackingResult => {
  const elementRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    const el = elementRef.current;
    const articleSlug = getArticleSlug();
    if (!ctaType || !el || hasFiredRef.current || typeof IntersectionObserver === 'undefined' || !articleSlug) {
      return;
    }

    let visibleTimer: ReturnType<typeof setTimeout> | undefined;
    let isAboveThreshold = false;

    const clearVisibleTimer = () => {
      clearTimeout(visibleTimer);
      visibleTimer = undefined;
    };

    const startVisibleTimer = () => {
      if (visibleTimer !== undefined) {
        return;
      }

      visibleTimer = setTimeout(() => {
        hasFiredRef.current = true;
        observer.disconnect();
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        const eventPayload: {
          event: string;
          article_slug?: string;
          cta_position?: ArticleCtaPosition;
          cta_type: CtaType;
        } = {
          event: 'cta_view',
          cta_type: ctaType,
          article_slug: articleSlug,
        };
        if (ctaPosition !== undefined) {
          eventPayload.cta_position = ctaPosition;
        }

        window.dataLayer = window.dataLayer ?? [];
        window.dataLayer.push(eventPayload);
      }, MIN_VISIBLE_DURATION_MS);
    };

    // バックグラウンドタブでは実際には読者の目に触れていないため、
    // 非表示中はタイマーを止め、再表示後にあらためて1秒連続表示を計測し直す。
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearVisibleTimer();
        return;
      }
      if (isAboveThreshold) {
        startVisibleTimer();
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isAboveThreshold = entry.intersectionRatio >= VISIBLE_RATIO_THRESHOLD;
        if (!isAboveThreshold || document.visibilityState === 'hidden') {
          clearVisibleTimer();
          return;
        }
        startVisibleTimer();
      },
      { threshold: VISIBLE_RATIO_THRESHOLD }
    );

    observer.observe(el);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearVisibleTimer();
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [ctaType, ctaPosition]);

  return elementRef;
};
